import React, { useEffect } from 'react'
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
} from 'antd'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import {
  organizationRolesActions,
  useOrganizationRoleSelector,
} from 'store/organizationRolesSlice/OrganizationRolesReducer'
import { useDispatch } from 'react-redux'
import {
  deleteRole,
  getOrganizationUsers,
  getRole,
  updateRole,
  updateRoleStatus,
} from 'store/organizationRolesSlice/OrganizationRolesActions'
import { useAuthSelector } from 'store/authSlice/authReducer'
import {
  ComponentPermissionGroup,
  OrganizationPermissionGroup,
} from 'permissions'
import { config } from 'configs'

const OrganizationRoles = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { organization, subscription } = useAuthSelector()
  const { loading, roleList, role } = useOrganizationRoleSelector()
  const {
    fetchRoleOptions,
    fetchRoleList,
    fetchRole,
    updating,
    changingStatus,
    deleting,
  } = loading
  const filteredRoleList = roleList?.filter(
    (item) => item.Roles?.includes(config.organizationAdmin) === false,
  )

  const getUsers = () => {
    if (organization?.id) {
      dispatch(getOrganizationUsers(organization?.id))
    }
  }

  useEffect(() => {
    getUsers()

    // on unmount reset the state
    return () => {
      dispatch(organizationRolesActions.reset())
    }
    // eslint-disable-next-line
  }, [])

  const columns = [
    {
      // title: 'Component',
      dataIndex: 'ProfileTitle',
      className: 'ant-table-row-cell-break-word',
      width: '60%',
      render: (_, item) => (
        <>
          <p className="t-text-[#141414] t-mb-[0.5]">{item?.Label}</p>
          <small>{item?.Description}</small>
        </>
      ),
    },
    {
      title: 'Reader',
      dataIndex: 'OrganizationName',
      className: 'ant-table-row-cell-break-word',
      render: (_, item) => {
        const cellName = `${item?.Component}Reader`
        const hasEditorPermission = role?.Roles?.includes(
          `${item?.Component}Editor`,
        )
        return (
          <>
            {updating === cellName ? (
              <Spin />
            ) : (
              <Checkbox
                disabled={isCellDisabled || hasEditorPermission}
                checked={hasEditorPermission || role?.Roles?.includes(cellName)}
                onChange={(e) =>
                  handleCheckboxChange(e.target.checked, cellName)
                }
              />
            )}
          </>
        )
      },
    },
    {
      title: 'Editor',
      dataIndex: 'OrganizationEmail',
      className: 'ant-table-row-cell-break-word',
      render: (_, item) => {
        const cellName = `${item?.Component}Editor`
        return (
          <>
            {updating === cellName ? (
              <Spin />
            ) : (
              <Checkbox
                disabled={isCellDisabled}
                checked={role?.Roles?.includes(cellName)}
                onChange={(e) =>
                  handleCheckboxChange(e.target.checked, cellName)
                }
              />
            )}
          </>
        )
      },
    },
  ]

  const handleCheckboxChange = (value, cellName) => {
    const payload = {
      OrganizationId: role?.OrganizationId,
      UserId: role?.UserId,
      Role: cellName,
    }
    if (value) {
      payload.Action = 'Add'
    } else {
      payload.Action = 'Remove'
    }
    dispatch(updateRole(payload))
  }

  const handleSelctedUserChange = (value) => {
    if (!value) {
      dispatch(organizationRolesActions.setRole(null))
    } else {
      const user = filteredRoleList?.find((item) => item.RoleId === value)
      dispatch(getRole(user?.OrganizationId, user?.UserId))
    }
  }

  const handleDelete = () => {
    const payload = {
      OrganizationId: role?.OrganizationId,
      UserId: role?.UserId,
    }
    dispatch(deleteRole(payload, getUsers))
  }

  const handleChangeActiveStatus = () => {
    const payload = {
      OrganizationId: role?.OrganizationId,
      UserId: role?.UserId,
      Action: role?.ActiveInd === 1 ? 'Disable' : 'Enable',
    }
    dispatch(updateRoleStatus(payload))
  }

  const isRoleActive = role?.ActiveInd === 1
  const isCellDisabled =
    !role || !isRoleActive || updating || changingStatus || deleting
  const selectedUserName = filteredRoleList?.find(
    (item) => item?.RoleId === role?.RoleId,
  )?.UserName

  const allowedModules = subscription?.Tier?.IncludedModules || []
  const filteredComponentPermissionGroup = ComponentPermissionGroup?.filter(
    (item) => allowedModules.includes(item.Component),
  )
  return (
    <Card
      id="notifications"
      className="mb-24 header-solid"
      bordered={false}
      title={[
        <>
          <h5 className="font-semibold"></h5>
          <div className="t-flex t-items-center t-space-x-2">
            <h6 className="font-semibold t-text-2xl">
              Assign Organization Roles
            </h6>
            <Button
              type="primary"
              size="small"
              onClick={() => history.push('/organization/role/add-user')}
            >
              Add User
            </Button>
          </div>
          <p className="font-regular">
            Add User and manage permissions for roles
          </p>
        </>,
      ]}
    >
      <Skeleton
        loading={fetchRoleOptions || fetchRoleList}
        active
        paragraph={{ rows: 16 }}
      >
        <Space wrap>
          <Select
            value={fetchRole ? 'Loading ...' : selectedUserName}
            style={{ minWidth: 250 }}
            showSearch
            optionFilterProp="children"
            allowClear
            placeholder="Select User"
            onChange={handleSelctedUserChange} // Use the same handler or modify it as needed
          >
            {filteredRoleList?.map((item, index) => (
              <Select.Option value={item.RoleId} key={index}>
                {item.UserName}
              </Select.Option>
            ))}
          </Select>

          {(fetchRole || role) && (
            <Space>
              <Button
                disabled={fetchRole}
                loading={changingStatus}
                onClick={() => handleChangeActiveStatus()}
              >
                {isRoleActive ? 'Disable' : 'Enable'}
              </Button>
              <Button
                disabled={fetchRole}
                loading={deleting}
                onClick={() => handleDelete()}
              >
                Delete
              </Button>
            </Space>
          )}
        </Space>
        {selectedUserName && (
          <Space className="t-mt-8">
            <Descriptions>
              <Descriptions.Item label="Phone Number" span={3}>
                {role?.UserPhoneNumber}
              </Descriptions.Item>
              {/* <Descriptions.Item label="Email" span={3}>
                {role?.UserEmail}
              </Descriptions.Item> */}
            </Descriptions>
          </Space>
        )}
        <Skeleton loading={fetchRole} active paragraph={{ rows: 14 }}>
          <Divider />
          <h5 className="t-font-semibold">Component</h5>
          <Table
            columns={columns}
            dataSource={filteredComponentPermissionGroup || []}
            className="table-settings-notifications"
            tableLayout="auto"
            bordered={false}
            pagination={false}
          />
          <Divider />
          <h5 className="t-font-semibold">Organization</h5>
          <Table
            columns={columns}
            dataSource={OrganizationPermissionGroup || []}
            className="table-settings-notifications"
            tableLayout="auto"
            bordered={false}
            pagination={false}
          />
        </Skeleton>
      </Skeleton>
    </Card>
  )
}

export { OrganizationRoles }
